///
/// EarthBrowser
///
/// Created by Matt Giger on 1/28/2024
/// Copyright (c) 2024 Geodata Labs
///

import { Camera } from "@stovokor/camera/Camera"
import { Orthographic } from "@stovokor/camera/Orthographic"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { type RenderActionSet } from "@stovokor/engine/Renderer"
import { RenderState } from "@stovokor/engine/RenderState"
import { type GestureHitResult } from "@stovokor/gestures/GestureRecognizer"
import { Pane } from "@stovokor/uipanes/Pane"
import { StovokorContext } from "../StovokorContext"

/**
 * Interface for UI viewport gesture hit testing
 *
 * UI viewports use 2D coordinate hit testing rather than 3D ray casting.
 * Hit results are returned with gesture recognizers from panes that
 * contain the touch point.
 */
export interface UIViewportInterface {
	readonly camera: Camera
	readonly width: number
	readonly height: number
	gestureHitTest2D(x: number, y: number): GestureHitResult[]
}

export class UIViewport extends NodeBase implements UIViewportInterface {

	protected context: StovokorContext

	public camera = new Camera(new Orthographic(0, 100, 100, 0, -1000, 1000))
	public clearDepthOnRender: boolean = true

	private _width: number = 0
	private _height: number = 0
	public get width(): number { return this._width }
	public get height(): number { return this._height }

	// Reused across frames, see WorldViewport
	private readonly renderState = new RenderState(this.camera)
	private readonly opaque: RenderActionSet[] = []
	private readonly transparent: RenderActionSet[] = []

	constructor(context: StovokorContext) {
		super()
		this.context = context
	}

	public size(width: number, height: number): void {
		if(width != this._width || height != this._height) {
			const deltaWidth = width - this._width
			const deltaHeight = height - this._height
			this._width = width
			this._height = height
			const paneChildren = this._children.filter(c => c instanceof Pane)
			paneChildren.forEach(c => c.parentResized(width, height, deltaWidth, deltaHeight))

			if(width > 0 && height > 0) {
				this.camera.projection.setSize(width, height)
				this.camera.updateProjection()
			}
		}
	}

	override addNode<T extends NodeBase>(child: T | T[]): T {
		const paneChildren = this._children.filter(c => c instanceof Pane)
		paneChildren.forEach(c => c.parentResized(this._width, this._height, 0, 0))
		return super.addNode(child)
	}

	public renderViewport(): void {
		if(!this.visible) {
			return
		}

		const gl = this.context.gl

		// don't clear the color buffer, we usually want to render on top of a 3d world.
		// gl.clear honours the depth write mask, and the previous viewport's
		// transparent pass leaves it off, so re-enable writes first or the
		// clear is a no-op and this pass depth-tests against the label pass
		// (hover cards lose to city labels wherever a glyph was drawn)
		if(this.clearDepthOnRender) {
			this.context.glState.depthMask(true)
			gl.clear(WebGL2RenderingContext.DEPTH_BUFFER_BIT)
		}

		const state = this.renderState
		state.beginFrame(this.camera, this.context.frameTime)
		state.pixelRatio = this.context.pixelRatio
		const actions: RenderActionSet[] = this.renderChildren(state)

		const opaque = this.opaque
		const transparent = this.transparent
		for(let i = 0; i < actions.length; i++) {
			const set = actions[i]
			if(set.transparency) {
				transparent.push(set)
			} else {
				opaque.push(set)
			}
		}

		// Panes report their layering z as depth (larger = frontmost), drawn
		// front-to-back in both passes so nearer panes win the depth test
		const glState = this.context.glState
		glState.passDepthWrite = true
		glState.depthMask(true)
		opaque.sort((a, b) => (a.order - b.order) || (b.depth - a.depth))
		for(let i = 0; i < opaque.length; i++) {
			opaque[i].render()
		}

		// Disable depth writing for transparent objects to prevent incorrect occlusion
		// They still read from depth buffer (depth test) but don't write to it
		glState.passDepthWrite = false
		glState.depthMask(false)
		transparent.sort((a, b) => (a.order - b.order) || (b.depth - a.depth))
		for(let i = 0; i < transparent.length; i++) {
			transparent[i].render()
		}
		glState.passDepthWrite = true
		glState.depthMask(true)
		opaque.length = 0
		transparent.length = 0
		glState.bindVertexArray(null)
	}

	/**
	 * Gesture hit test for 2D UI elements
	 *
	 * Uses simple 2D containment testing rather than 3D ray casting.
	 * Returns hits sorted by depth (closest/frontmost first).
	 *
	 * Note: This is a different signature from NodeBase.gestureHitTest which uses RenderState.
	 * The UIViewportInterface defines this 2D-specific hit test method.
	 */
	public gestureHitTest2D(x: number, y: number): GestureHitResult[] {
		if(!this.visible) {
			return []
		}

		const hits: GestureHitResult[] = []
		this.collectPaneHits(this._children, x, y, hits)

		// Sort by distance (lower = closer/frontmost in 2D)
		return hits.sort((a, b) => a.distance - b.distance)
	}

	/**
	 * Recursively collect gesture hits from pane hierarchy
	 */
	private collectPaneHits(nodes: NodeBase[], x: number, y: number, hits: GestureHitResult[]): void {
		for (const node of nodes) {
			if (node instanceof Pane && node.visible) {
				const hit = node.gestureHitTest2D(x, y)
				if (hit) {
					hits.push(hit)
				}
				// Check children recursively
				this.collectPaneHits((node as NodeBase)["_children"], x, y, hits)
			}
		}
	}
}
